import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { deleteEvent, getUpcomingEvents, getPastEvents } from '../../services/api';

const FORMAT_LABELS = {
  in_person: 'In-person',
  online: 'Online',
  hybrid: 'Hybrid',
};

function AdminEventsList() {
  const [tab, setTab] = useState('upcoming');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '—';
    const amount = Number(value);
    if (Number.isNaN(amount)) return '—';
    if (amount === 0) return 'Free';
    return `$${amount.toFixed(2)}`;
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = tab === 'upcoming' ? await getUpcomingEvents() : await getPastEvents();
        setEvents(data.results || data);
      } catch (e) {
        setError(e.message || 'Failed to load events');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tab]);

  useEffect(() => {
    if (location.state?.success) {
      setFlash(location.state.success);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const handleDelete = async (eventId) => {
    const confirmed = window.confirm('Are you sure you want to delete this event?');
    if (!confirmed) return;

    try {
      setDeletingId(eventId);
      setError('');
      await deleteEvent(eventId);
      setEvents((prev) => prev.filter((ev) => ev.id !== eventId));
      setFlash('Event deleted successfully.');
    } catch (deleteError) {
      setError(deleteError.message || 'Failed to delete event');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Admin • Events</h1>
        <Link to="/admin/events/new" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">New Event</Link>
      </div>

      <div className="flex gap-4 border-b border-gray-200 mb-4">
        {['upcoming','past'].map(key => (
          <button key={key} className={`py-2 px-3 border-b-2 ${tab===key?'border-blue-600 text-blue-700':'border-transparent text-gray-600 hover:border-gray-300'}`} onClick={() => setTab(key)}>
            {key === 'upcoming' ? 'Upcoming' : 'Past'}
          </button>
        ))}
      </div>

      {flash && <div className="text-green-600 mb-3">{flash}</div>}
      {error && <div className="text-red-600 mb-3">{error}</div>}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="overflow-auto bg-white rounded shadow">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Start</th>
                <th className="px-4 py-2">Format</th>
                <th className="px-4 py-2">Location</th>
                <th className="px-4 py-2">Capacity</th>
                <th className="px-4 py-2">Spots</th>
                <th className="px-4 py-2">Pricing</th>
                <th className="px-4 py-2">Published</th>
                <th className="px-4 py-2">Active</th>
                <th className="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map(ev => (
                <tr key={ev.id} className="border-t">
                  <td className="px-4 py-2 font-medium">{ev.name}</td>
                  <td className="px-4 py-2">{new Date(ev.start_date).toLocaleString()}</td>
                  <td className="px-4 py-2">{FORMAT_LABELS[ev.event_format] || 'In-person'}</td>
                  <td className="px-4 py-2">{ev.event_format === 'online' ? 'Online' : ev.location}</td>
                  <td className="px-4 py-2">{ev.capacity}</td>
                  <td className="px-4 py-2">{typeof ev.available_spots === 'number' ? ev.available_spots : '-'}</td>
                  <td className="px-4 py-2">
                    <div className="space-y-1">
                      <div>Community: {formatCurrency(ev.base_price)}</div>
                      <div className="text-sm text-gray-600">Pro: {formatCurrency(ev.pro_member_price ?? ev.base_price)}</div>
                    </div>
                  </td>
                  <td className="px-4 py-2">{ev.is_published ? 'Yes' : 'No'}</td>
                  <td className="px-4 py-2">{ev.is_active ? 'Yes' : 'No'}</td>
                  <td className="px-4 py-2">
                    <div className="flex gap-2">
                      <Link className="text-blue-600" to={`/admin/events/${ev.id}`}>Edit</Link>
                      <Link className="text-blue-600" to={`/admin/events/${ev.id}/registrations`}>Registrations</Link>
                      <button
                        type="button"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDelete(ev.id)}
                        disabled={deletingId === ev.id}
                      >
                        {deletingId === ev.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {events.length === 0 && (
                <tr><td className="px-4 py-6 text-gray-600" colSpan="10">No events found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminEventsList;
